import { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Stack, Typography } from '@mui/material';
import FileManagement from '@components/file-management';
import CustomerDeliveryFilesFilter from '@components/filters/customer-delivery-files';
import { ROUTES } from 'constants/routes';
import { customerDeliveryFilesListColumns } from './list-data';
import completedUploadStatusIcon from '@assets/images/table/completedUploadStatusIcon.svg';
import failedUploadStatusIcon from '@assets/images/table/failedUploadStatusIcon.svg';

import {
  CustomerDeliveryFileStatus,
  CustomerDeliveryFileUploadStatus,
} from 'types';

import { getCustomerDeliveryFileList } from 'shared/helpers/customer-delivery/get-customer-delivery-file-List';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkPayloadObject } from 'shared/helpers/checkPayloadObject';

type ListDataType = {
  page: number;
  size: number;
  adminConfigEnv: {
    cssJsVersion: string;
  } | null;
  sortName:
  | ''
  | 'otmSubject'
  | 'lfsUploadSt'
  | 'otmfOriginName'
  | 'otmSt'
  | 'expirationYmd'
  | 'regDt'
  | null;
  sortType: 'asc' | 'dsc' | '' | null;
  schOtmSt: CustomerDeliveryFileUploadStatus | '';
  schGroup: string;
  schKeyword: string;
  otmList:
  | [
    {
      otmSeq: string;
      otmSubject: string;
      otmfOriginName: null | string;
      otmfSize: number;
      otmSt: CustomerDeliveryFileStatus;
      expirationYmd: string;
      regDt: string;
      lfsUploadSt: CustomerDeliveryFileUploadStatus;
      regName: null | string;
    },
  ]
  | [];
  totalCount: number;
};

const CustomerDeliveryFilesViewPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [reload, setReload] = useState(false);
  const [isLoading, setLoading] = useState<boolean>(false)
  const initialData: ListDataType = {
    page: 1,
    size: 10,
    sortName: '',
    sortType: '',
    schOtmSt: '',
    schGroup: '',
    schKeyword: '',
    adminConfigEnv: null,
    otmList: [],
    totalCount: 0,
  }
  const [listData, setListData] = useState<ListDataType>(initialData);

  useEffect(() => {
    const isRefresh = location?.state?.refresh
    const fetchData = async () => {
      setLoading(true)
      await getCustomerDeliveryFileList(initialData,
        // @ts-ignore
        [{ key: 'page', value: 1 }, { key: 'schKeyword', value: '' }, { key: 'schOtmSt', value: '', }, { key: 'schGroup', value: 'subject_filename' }],
        setListData,
        ['page', 'schKeyword', 'schOtmSt', 'schGroup'])
      setLoading(false)
    }

    if (isRefresh) {
      fetchData()
    }
  }, [location?.state])

  const lfsUploadSt = {
    field: 'lfsUploadSt',
    headerName: '업로드 상태', // Upload Status
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    maxWidth: 120,
    minWidth: 120,
    renderCell: (params: GridRenderCellParams) => {
      const value = params.value;
      return (
        <Box display="inline-block">
          <Box display="flex" flexDirection="row" alignItems="left" gap={1}>
            {value === '60' && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: '14px' }}>
                <img
                  src={failedUploadStatusIcon}
                  alt="failed"
                  width={16}
                  height={16}
                />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="var(--danger-500)"
                >
                  업로드 실패
                </Typography>
              </Stack>
            )}
            {value === '50' && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: '14px' }}>
                <img
                  src={completedUploadStatusIcon}
                  alt="success"
                  width={16}
                  height={16}
                />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="var(--success-500)"
                >
                  업로드 완료
                </Typography>
              </Stack>
            )}
            {value === '90' && (
              <Typography
                variant="body2"
                fontWeight={500}
                color="var(--neutral-800)"
              >
                만료(파일삭제됨)
              </Typography>
            )}
          </Box>
        </Box>
      );
    },
  };

  const columns = [
    ...customerDeliveryFilesListColumns.slice(0, 2),
    lfsUploadSt,
    ...customerDeliveryFilesListColumns.slice(2),
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      await getCustomerDeliveryFileList(listData, [], setListData, []);
      setLoading(false)
    }
    fetchData()
  }, [reload]);

  return (
    <FileManagement
      isLoading={isLoading}
      title="고객 전달용 대용량 파일"
      linkStack={[
        {
          label: 'Home',
          path: ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
        },
        {
          label: '고객 전달용 대용량 파일',
          path: ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
        },
      ]}
      Filter={
        <CustomerDeliveryFilesFilter
          getFilteredList={async (payload) => {
            setLoading(true)
            await getCustomerDeliveryFileList(listData, payload, setListData, [
              'page',
              'schOtmSt',
              'schGroup',
              'schKeyword',
            ])
            if (!checkPayloadObject(payload)) {
              navigate(
                ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
                { state: { page: payload[0].value, notEmpty: true } }
              )
            }
            setLoading(false)
          }}
          isLoading={isLoading}
          isCreateAction={true}
          createActionPath={ROUTES.CUSTOMER_DELIVERY_FILES.REGISTER_FILE.path}
        />
      }
      isCreateAction={true}
      createActionPath={ROUTES.CUSTOMER_DELIVERY_FILES.REGISTER_FILE.path}
      type="CUSTOMER_DELIVERY"
      listData={{
        columns,
        rows: listData?.otmList?.map((item, index) => {
          return {
            id:
              listData.totalCount - (listData.page - 1) * listData.size - index,
            ...item,
          };
        }),
        totalCount: listData.totalCount,
        page: listData.page,
        pageSize: listData.size,
        serviceRequiredData: listData,
        setServiceRequiredData: setListData,
      }}
      getFilteredList={(payload) =>
        getCustomerDeliveryFileList(listData, payload, setListData, [
          'sortName',
          'sortType',
        ])
      }
      handlePagination={async (payload) => {
        await getCustomerDeliveryFileList(listData, payload, setListData, ['page'])
        if (!checkPayloadObject(payload)) {
          navigate(
            ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
            { state: { page: payload[0].value, notEmpty: true } }
          )
        }
      }}
      setReload={setReload}
    />
  );
};

export default CustomerDeliveryFilesViewPage;
